import { SelectInput, SelectOption } from '@/components/form/SelectInput';
import { Button } from '@/components/shared/button/Button';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { useTemplatesForLocation } from '@/services/shared/useTemplatesForLocation';
import { TemplateOption } from '@/types/template.types';
import { FC, useState } from 'react';
import { useReplaceRoster } from '../hooks/useReplaceRoster';
import { useWorkingRoster } from '../providers/WorkingRosterService';
import CreateTemplateFromRosterModal from './CreateTemplateFromRosterModal';

export const RosterTemplates: FC = () => {
  const { workingRoster } = useWorkingRoster();

  if (!workingRoster) throw new Error('Working roster not found');

  const { templateOptions } = useTemplatesForLocation();
  const replaceRoster = useReplaceRoster();

  const [pendingTemplate, setPendingTemplate] = useState<TemplateOption | null>(null);

  const options: SelectOption[] = templateOptions.map(t => ({
    name: t.name,
    value: t.id
  }));

  const handleChange = (templateId: string) => {
    const template = templateOptions.find(t => t.id === templateId);
    if (!template || template.id === workingRoster?.templateId) return;
    setPendingTemplate(template);
    setConfirmReplaceModalOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingTemplate) return;
    replaceRoster(pendingTemplate.id);
    setPendingTemplate(null);
    setConfirmReplaceModalOpen(false);
  };

  const [confirmReplaceModalOpen, setConfirmReplaceModalOpen] = useState(false);
  const [createTemplateModalOpen, setCreateTemplateModalOpen] = useState(false);

  return (
    <>
      <div className="flex flex-row justify-between mt-2">
        <div className="space-y-1.5">
          <p className="font-semibold text-xl">Current Template</p>

          <SelectInput
            options={options}
            value={workingRoster?.templateId ?? ''}
            onChange={handleChange}
            name="template"
          />
        </div>

        <div>
          <Button
            onClick={() => {
              setCreateTemplateModalOpen(true);
            }}
            primary
          >
            Save As New Template
          </Button>
        </div>
      </div>

      {pendingTemplate && (
        <ConfirmDialog
          onConfirm={handleConfirm}
          open={confirmReplaceModalOpen}
          onClose={() => {
            setConfirmReplaceModalOpen(false);
          }}
          title="Replace Roster"
        >
          <div className="space-y-1">
            <p>Are you sure you want to switch to {pendingTemplate.name}?</p>
            <p className="text-red-600 font-semibold">
              Changing this template will cause the roster to be completely overwritten with data from the new template.
              All existing shifts will be lost.
            </p>
          </div>
        </ConfirmDialog>
      )}

      <CreateTemplateFromRosterModal
        open={createTemplateModalOpen}
        onClose={() => setCreateTemplateModalOpen(false)}
        workingRoster={workingRoster}
      />
    </>
  );
};
